import { ChevronLeftIcon, StopIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  AnalyticsActivityData,
  AnalyticsChatRequest,
  AnalyticsErrorData,
  AnalyticsMessageData,
  AnalyticsScopeOption,
  AnalyticsScopeType,
  AnalyticsScopesResponse,
  AnalyticsSseEvent,
  AnalyticsTurnData,
  askAnalyticsQuestion,
  clearAnalyticsConversation,
  getAnalyticsConversation,
  getAnalyticsScopes,
  parseAnalyticsEventData,
  stopAnalyticsTurn,
  streamAnalyticsTurn,
} from "@/api/rest/services/analyticsService";
import AnalyticsAssistantTurnCard from "@/app/analytics/AnalyticsAssistantTurnCard";
import {
  AnalyticsAssistantTurn,
  AnalyticsConversationItem,
  applyActivityToTurn,
  applyMessageToTurn,
  applySnapshotToTurn,
  createAssistantTurn,
  createAssistantTurnFromSnapshot,
  finalizeAssistantTurn,
  finalizeIfUnterminated,
  hydrateAnalyticsConversation,
} from "@/app/analytics/analyticsTurnState";
import Button from "@/components/common/Button";
import { ChatMessage, ChatMessageHolder, ChatMessageType } from "@/components/common/ChatMessage";
import Spinner from "@/components/common/Spinner";

interface ScopeOption {
  type: AnalyticsScopeType;
  label: string;
  value?: string;
}

const PRESET_QUESTIONS = [
  "Show recent user growth for this company",
  "What are the top 5 buildings by case count?",
  "How many open cases are there right now?",
  "What changed in the last 30 days?",
];

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [messages, setMessages] = useState<AnalyticsConversationItem[]>([]);
  const [streamingTurn, setStreamingTurn] = useState<AnalyticsAssistantTurn | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [isScopesLoading, setIsScopesLoading] = useState(true);
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  const [isClearingConversation, setIsClearingConversation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [scopesResponse, setScopesResponse] = useState<AnalyticsScopesResponse | null>(null);
  const [scopesError, setScopesError] = useState<string | null>(null);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [selectedScopeKey, setSelectedScopeKey] = useState("");
  const [conversationId, setConversationId] = useState(createConversationId);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const scrollRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamingTurnRef = useRef<AnalyticsAssistantTurn | null>(null);

  const availableScopes = useMemo(() => buildScopeOptions(scopesResponse), [scopesResponse]);
  const selectedScope = useMemo(
    () => availableScopes.find((scope) => getScopeKey(scope) === selectedScopeKey) ?? null,
    [availableScopes, selectedScopeKey],
  );

  const scrollToBottom = useCallback(() => {
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current?.scrollHeight), 100);
  }, []);

  const setStreamingTurnState = useCallback((nextTurn: AnalyticsAssistantTurn | null) => {
    streamingTurnRef.current = nextTurn;
    setStreamingTurn(nextTurn);
  }, []);

  const updateStreamingTurn = useCallback(
    (updater: (current: AnalyticsAssistantTurn | null) => AnalyticsAssistantTurn | null) => {
      const nextTurn = updater(streamingTurnRef.current);
      setStreamingTurnState(nextTurn);
    },
    [setStreamingTurnState],
  );

  const isStreamingActive = Boolean(streamingTurn && !streamingTurn.completedAt);

  useEffect(() => {
    let isActive = true;

    const loadScopes = async () => {
      setIsScopesLoading(true);
      setScopesError(null);

      try {
        const response = await getAnalyticsScopes();
        if (!isActive) {
          return;
        }

        setScopesResponse(response);
        setSelectedScopeKey(getDefaultScopeKey(response));
      } catch (error) {
        if (!isActive) {
          return;
        }
        setScopesError(error instanceof Error ? error.message : "Failed to load analytics scopes");
      } finally {
        if (isActive) {
          setIsScopesLoading(false);
        }
      }
    };

    void loadScopes();

    return () => {
      isActive = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!isStreamingActive) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isStreamingActive]);

  const finalizeStreamingTurn = useCallback(
    (updater?: (current: AnalyticsAssistantTurn) => AnalyticsAssistantTurn) => {
      const finalizedTurn = finalizeAssistantTurn(streamingTurnRef.current, updater);
      setStreamingTurnState(null);

      if (finalizedTurn) {
        setMessages((previous) => [...previous, finalizedTurn]);
      }
    },
    [setStreamingTurnState],
  );

  const handleEvent = useCallback(
    (event: AnalyticsSseEvent) => {
      switch (event.event) {
        case "snapshot": {
          const snapshot = parseAnalyticsEventData<AnalyticsTurnData>(event.data);
          if (!snapshot?.turnId) {
            break;
          }

          setMessages((previous) => {
            const lastMessage = previous[previous.length - 1];
            if (lastMessage?.role !== "user") {
              return previous;
            }
            if (lastMessage.content === snapshot.userMessage) {
              return previous;
            }
            return [...previous.slice(0, -1), { role: "user", content: snapshot.userMessage }];
          });
          updateStreamingTurn((current) => applySnapshotToTurn(current, snapshot));
          setNowMs(Date.now());
          scrollToBottom();
          break;
        }
        case "activity": {
          const activityData = parseAnalyticsEventData<AnalyticsActivityData>(event.data);
          if (!activityData?.id) {
            break;
          }

          updateStreamingTurn((current) => {
            return applyActivityToTurn(current, activityData);
          });
          setNowMs(Date.now());
          scrollToBottom();
          break;
        }
        case "message": {
          const messageData = parseAnalyticsEventData<AnalyticsMessageData>(event.data);
          if (typeof messageData?.content !== "string") {
            break;
          }

          updateStreamingTurn((current) => {
            return applyMessageToTurn(current, messageData.content);
          });
          scrollToBottom();
          break;
        }
        case "error": {
          const errorData = parseAnalyticsEventData<AnalyticsErrorData>(event.data);
          const errorMessage = errorData?.message ?? "Analytics request failed";
          finalizeStreamingTurn((current) => ({
            ...current,
            error: `Error: ${errorMessage}`,
            finalAnswer: current.finalAnswer,
            completedAt: Date.now(),
          }));
          setIsLoading(false);
          scrollToBottom();
          break;
        }
        case "done": {
          finalizeStreamingTurn((current) => ({
            ...current,
            completedAt: Date.now(),
          }));
          setIsLoading(false);
          scrollToBottom();
          break;
        }
      }
    },
    [finalizeStreamingTurn, scrollToBottom, updateStreamingTurn],
  );

  // Streams one turn. A stream that closes without done/error (proxy cut, deploy) would leave the
  // input locked and the timer running, so end the turn with "Connection lost" if it is still ours.
  const streamTurn = useCallback(
    async (
      stream: (onEvent: (event: AnalyticsSseEvent) => void, signal: AbortSignal) => Promise<void>,
      abortController: AbortController,
    ) => {
      let sawTerminalEvent = false;
      await stream((event) => {
        if (event.event === "done" || event.event === "error") {
          sawTerminalEvent = true;
        }
        handleEvent(event);
      }, abortController.signal);

      if (abortControllerRef.current !== abortController) {
        return;
      }
      const lostTurn = finalizeIfUnterminated(streamingTurnRef.current, sawTerminalEvent);
      if (lostTurn) {
        setStreamingTurnState(null);
        setMessages((previous) => [...previous, lostTurn]);
        setIsLoading(false);
      }
    },
    [handleEvent, setStreamingTurnState],
  );

  const connectToExistingTurn = useCallback(
    async (turnId: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        await streamTurn((onEvent, signal) => streamAnalyticsTurn(turnId, onEvent, signal), abortController);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }

        finalizeStreamingTurn((current) => ({
          ...current,
          error: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
          completedAt: Date.now(),
        }));
      } finally {
        setIsLoading(false);
      }
    },
    [finalizeStreamingTurn, streamTurn],
  );

  useEffect(() => {
    if (isScopesLoading || scopesError || !scopesResponse?.canAccess || !selectedScope) {
      return;
    }

    let isActive = true;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setStreamingTurnState(null);
    setIsLoading(false);
    setIsConversationLoading(true);
    setConversationError(null);

    const loadConversation = async () => {
      try {
        const response = await getAnalyticsConversation(
          selectedScope.type,
          selectedScope.type === "partner_code" ? selectedScope.value : undefined,
        );
        if (!isActive) {
          return;
        }

        const hydratedMessages = hydrateAnalyticsConversation(response.messages);
        const activeTurn = response.activeTurn;
        setConversationId(response.conversationId ?? createConversationId());
        setMessages(
          activeTurn ? [...hydratedMessages, { role: "user", content: activeTurn.userMessage }] : hydratedMessages,
        );
        setStreamingTurnState(activeTurn ? createAssistantTurnFromSnapshot(activeTurn) : null);
        setInputValue("");
        setNowMs(Date.now());
        setIsLoading(activeTurn?.status === "queued" || activeTurn?.status === "running");
        scrollToBottom();

        if (activeTurn?.status === "queued" || activeTurn?.status === "running") {
          void connectToExistingTurn(activeTurn.turnId);
        }
      } catch (error) {
        if (!isActive) {
          return;
        }

        setConversationId(createConversationId());
        setMessages([]);
        setConversationError(error instanceof Error ? error.message : "Failed to load saved analytics chat");
      } finally {
        if (isActive) {
          setIsConversationLoading(false);
        }
      }
    };

    void loadConversation();

    return () => {
      isActive = false;
    };
  }, [
    connectToExistingTurn,
    isScopesLoading,
    scopesError,
    scopesResponse?.canAccess,
    scrollToBottom,
    selectedScope,
    setStreamingTurnState,
  ]);

  const submitQuestion = useCallback(
    async (question: string) => {
      if (!question.trim() || isLoading || !selectedScope) {
        return;
      }

      const trimmedQuestion = question.trim();
      const analyticsRequest: AnalyticsChatRequest = {
        conversationId,
        scopeType: selectedScope.type,
        scopeValue: selectedScope.type === "partner_code" ? selectedScope.value : undefined,
        message: trimmedQuestion,
      };

      setMessages((previous) => [...previous, { role: "user", content: trimmedQuestion }]);
      setInputValue("");
      setIsLoading(true);
      setNowMs(Date.now());
      const initialAssistantTurn = createAssistantTurn();
      setStreamingTurnState(initialAssistantTurn);
      scrollToBottom();

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        await streamTurn((onEvent, signal) => askAnalyticsQuestion(analyticsRequest, onEvent, signal), abortController);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          setIsLoading(false);
          return;
        }

        finalizeStreamingTurn((current) => ({
          ...current,
          error: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
          completedAt: Date.now(),
        }));
        setIsLoading(false);
      }
    },
    [
      conversationId,
      finalizeStreamingTurn,
      isLoading,
      scrollToBottom,
      selectedScope,
      setStreamingTurnState,
      streamTurn,
    ],
  );

  const handleSubmit = useCallback(() => {
    void submitQuestion(inputValue);
  }, [inputValue, submitQuestion]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleScopeChange = useCallback(
    (newScopeKey: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      setStreamingTurnState(null);
      setIsLoading(false);
      setMessages([]);
      setInputValue("");
      setConversationId(createConversationId());
      setConversationError(null);
      setSelectedScopeKey(newScopeKey);
    },
    [setStreamingTurnState],
  );

  const handleStopTurn = useCallback(async () => {
    const turnId = streamingTurnRef.current?.turnId;
    if (!turnId || isStopping) {
      return;
    }

    setIsStopping(true);
    try {
      const snapshot = await stopAnalyticsTurn(turnId);
      updateStreamingTurn((current) => applySnapshotToTurn(current, snapshot));
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      finalizeStreamingTurn((current) => ({
        ...current,
        status: "cancelled",
        completedAt: current.completedAt ?? Date.now(),
        error: undefined,
      }));
      setIsLoading(false);
    } catch (error) {
      setConversationError(error instanceof Error ? error.message : "Failed to stop analytics turn");
    } finally {
      setIsStopping(false);
    }
  }, [finalizeStreamingTurn, isStopping, updateStreamingTurn]);

  const handleClearConversation = useCallback(async () => {
    if (!selectedScope || isLoading || isConversationLoading || isClearingConversation) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsClearingConversation(true);
    setConversationError(null);

    try {
      await clearAnalyticsConversation(
        selectedScope.type,
        selectedScope.type === "partner_code" ? selectedScope.value : undefined,
      );
      setStreamingTurnState(null);
      setMessages([]);
      setInputValue("");
      setConversationId(createConversationId());
      setIsLoading(false);
      setNowMs(Date.now());
    } catch (error) {
      setConversationError(error instanceof Error ? error.message : "Failed to clear analytics chat");
    } finally {
      setIsClearingConversation(false);
    }
  }, [isClearingConversation, isConversationLoading, isLoading, selectedScope, setStreamingTurnState]);

  const isBlocked = !isScopesLoading && !scopesError && !scopesResponse?.canAccess;
  const hasConversation = messages.length > 0 || Boolean(streamingTurn);

  return (
    <main className="scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-white flex flex-grow flex-col p-4 md:overflow-y-auto md:p-8">
      <div className="mx-auto flex min-h-0 w-full max-w-5xl flex-grow flex-col self-stretch">
        <div className="mb-6 flex flex-row items-center">
          <ChevronLeftIcon
            className="mr-2 h-9 w-9 cursor-pointer rounded hover:bg-slate-300 focus-visible:bg-dobu-orange"
            onClick={() => navigate(-1)}
          />
          <h2 className="mr-4 text-center text-3xl font-bold">{t("analytics.title")}</h2>
        </div>

        {isScopesLoading && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-5">
            <Spinner className="h-5 w-5" />
            <span className="text-slate-600">Loading analytics access…</span>
          </div>
        )}

        {scopesError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-red-700">{scopesError}</div>
        )}

        {isBlocked && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-5 text-slate-700">
            {scopesResponse?.accessError ?? "Analytics access is not available for this account."}
          </div>
        )}

        {conversationError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-red-700">
            {conversationError}
          </div>
        )}

        {!isScopesLoading && !scopesError && scopesResponse?.canAccess && selectedScope && (
          <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">Analytics scope</p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {isStreamingActive && streamingTurn?.turnId && (
                <Button
                  variant="outline"
                  onClick={handleStopTurn}
                  disabled={isStopping}
                  loading={isStopping}
                  className="gap-2"
                >
                  <StopIcon className="h-4 w-4" />
                  {t("analytics.stop")}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleClearConversation}
                disabled={
                  !hasConversation || isLoading || isConversationLoading || isClearingConversation || isStopping
                }
                loading={isClearingConversation}
                className="gap-2"
              >
                <TrashIcon className="h-4 w-4" />
                {t("analytics.clearChat")}
              </Button>

              {availableScopes.length > 1 ? (
                <select
                  value={selectedScopeKey}
                  onChange={(e) => handleScopeChange(e.target.value)}
                  disabled={isLoading || isConversationLoading || isClearingConversation}
                  className="min-w-56 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700"
                >
                  {availableScopes.map((scope) => (
                    <option key={getScopeKey(scope)} value={getScopeKey(scope)}>
                      {scope.label}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="self-start rounded-full border border-orange-200 bg-white px-4 py-2 text-sm text-slate-700 md:self-auto">
                  {selectedScope.label}
                </div>
              )}
            </div>
          </div>
        )}

        {!isBlocked &&
          !isScopesLoading &&
          !scopesError &&
          !isConversationLoading &&
          messages.length === 0 &&
          !streamingTurn && (
            <div className="mb-6">
              <p className="mb-3 text-gray-600">{t("analytics.tryAsking")}</p>
              <div className="flex flex-wrap gap-2">
                {PRESET_QUESTIONS.map((question, i) => (
                  <div
                    key={i}
                    className="rounded-full border border-orange-300 bg-white px-4 py-2 text-sm text-gray-600 hover:cursor-pointer hover:bg-orange-100"
                    onClick={() => void submitQuestion(question)}
                  >
                    {question}
                  </div>
                ))}
              </div>
            </div>
          )}

        <ChatMessageHolder scrollRef={scrollRef} className="mb-4 min-h-0 flex-1 gap-3 rounded-lg bg-slate-50 p-3">
          {isConversationLoading && (
            <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-5">
              <Spinner className="h-5 w-5" />
              <span className="text-slate-600">{t("analytics.loadingSavedChat")}</span>
            </div>
          )}
          {messages.map((message, index) =>
            message.role === "user" ? (
              <ChatMessage key={`user-${index}`} message={message.content} type={ChatMessageType.QUESTION} />
            ) : (
              <AnalyticsAssistantTurnCard key={`assistant-${index}`} turn={message} nowMs={nowMs} />
            ),
          )}
          {streamingTurn && (
            <AnalyticsAssistantTurnCard turn={streamingTurn} nowMs={nowMs} isStreaming={isStreamingActive} />
          )}
        </ChatMessageHolder>

        <div className="flex items-center gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("analytics.askQuestion")}
            disabled={
              !selectedScope ||
              isBlocked ||
              isScopesLoading ||
              isConversationLoading ||
              isClearingConversation ||
              Boolean(scopesError)
            }
            className="flex-1 rounded-md border border-gray-300 p-3 disabled:cursor-not-allowed disabled:bg-gray-100"
          />
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={
              isLoading ||
              !inputValue.trim() ||
              !selectedScope ||
              isBlocked ||
              isScopesLoading ||
              isConversationLoading ||
              isClearingConversation ||
              Boolean(scopesError)
            }
            loading={isLoading}
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </main>
  );
}

function buildScopeOptions(response: AnalyticsScopesResponse | null): ScopeOption[] {
  if (!response) {
    return [];
  }

  const partnerScopes: ScopeOption[] = response.scopes.map((scope: AnalyticsScopeOption) => ({
    type: "partner_code",
    value: scope.value,
    label: scope.label,
  }));

  if (response.canAccessAll) {
    return [{ type: "all", label: "(all)" }, ...partnerScopes];
  }

  return partnerScopes;
}

function getDefaultScopeKey(response: AnalyticsScopesResponse): string {
  if (response.canAccessAll) {
    return "all";
  }
  const firstScope = response.scopes[0];
  return firstScope ? getScopeKey({ type: "partner_code", value: firstScope.value, label: firstScope.label }) : "";
}

function getScopeKey(scope: ScopeOption): string {
  if (scope.type === "all") {
    return "all";
  }

  return `partner_code:${scope.value}`;
}

function createConversationId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `analytics-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
